import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";

import CommonPage from "../../components/common-page";
import MainDialog from "../../components/mainDialog";
import useLanguage from "../../hooks/useLanguage";
import useToaster from "../../hooks/useToaster";
import useOverlayTemplates from "../../hooks/useOverlayTemplates";
import OverlayTemplatesSkeleton from "./skeleton";
import OverlayTemplateEditor from "./editor";
import PresetsDialog from "./components/PresetsDialog";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function navigate(path) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function getSubPath() {
  const parts = window.location.pathname.split("/");
  const idx = parts.indexOf("overlay-templates");
  return parts[idx + 1] || "";
}

// ---------------------------------------------------------------------------
// List view
// ---------------------------------------------------------------------------
function OverlayTemplatesList() {
  const [{ pages }] = useLanguage();
  const t = pages.overlayTemplates || {};
  const { addToaster } = useToaster();
  const { list, remove, create } = useOverlayTemplates();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await list();
      setTemplates(Array.isArray(data) ? data : []);
    } catch {
      // error shown by hook
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleNew = () => setPresetsOpen(true);

  const handlePresetSelect = (presetKey) => {
    setPresetsOpen(false);
    window.history.pushState({ preset: presetKey }, "", "/overlay-templates/new");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  const handleEdit = (id) => navigate(`/overlay-templates/${id}`);

  const handleDuplicate = async (tpl) => {
    try {
      const result = await create({ name: `${tpl.name} (copy)`, canvas: tpl.canvas, elements: tpl.elements });
      addToaster({ message: t.duplicated || "Template duplicated", severity: "success" });
      setTemplates((prev) => [...prev, result]);
    } catch {
      // error shown by hook
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await remove(deleteTarget.id);
      setTemplates((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      addToaster({ message: t.deleted || "Template deleted", severity: "success" });
    } catch {
      // error shown by hook
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    return new Date(ts).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  if (loading) return <OverlayTemplatesSkeleton />;

  return (
    <CommonPage
      title={t.title || "Overlay Templates"}
      subtitle={t.subtitle || "Design and manage overlay layer templates for the reframer"}
      actions={[
        <Button key="new" variant="contained" color="action" startIcon={<AddIcon />} onClick={handleNew}>
          {t.new || "New Template"}
        </Button>,
      ]}
    >
      {templates.length === 0 ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            {t.empty || "No templates yet. Create one to get started."}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t.colName || "Name"}</TableCell>
                <TableCell align="center">{t.colElements || "Elements"}</TableCell>
                <TableCell>{t.colUpdated || "Updated"}</TableCell>
                <TableCell align="right">{t.colActions || "Actions"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...templates]
                .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
                .map((tpl) => (
                <TableRow key={tpl.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{tpl.name}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{tpl.elements?.length ?? 0}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{formatDate(tpl.updatedAt)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t.actionEdit || "Edit"}>
                      <IconButton size="small" onClick={() => handleEdit(tpl.id)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t.actionDuplicate || "Duplicate"}>
                      <IconButton size="small" onClick={() => handleDuplicate(tpl)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t.actionDelete || "Delete"}>
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(tpl)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PresetsDialog
        open={presetsOpen}
        onClose={() => setPresetsOpen(false)}
        onSelect={handlePresetSelect}
        t={t}
      />

      <MainDialog
        open={!!deleteTarget}
        title={t.deleteTitle || "Delete Template"}
        onClose={() => setDeleteTarget(null)}
        onClickAway={() => setDeleteTarget(null)}
        actions={[
          <Button key="cancel" onClick={() => setDeleteTarget(null)}>
            {t.cancel || "Cancel"}
          </Button>,
          <Button key="confirm" variant="contained" color="error" disabled={deleting} onClick={handleDeleteConfirm}>
            {deleting ? (t.deleting || "Deleting…") : (t.actionDelete || "Delete")}
          </Button>,
        ]}
      >
        <Typography>
          {deleteTarget
            ? (t.deleteConfirm || `Delete "${deleteTarget.name}"? This cannot be undone.`).replace("{name}", deleteTarget.name)
            : ""}
        </Typography>
      </MainDialog>
    </CommonPage>
  );
}

// ---------------------------------------------------------------------------
// Root: simple path-based router
// ---------------------------------------------------------------------------
export default function OverlayTemplates() {
  // re-render on popstate so navigating back/forward updates view
  const [, setTick] = useState(0);
  useEffect(() => {
    const handler = () => setTick((t) => t + 1);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  const sub = getSubPath();
  if (sub === "new" || (sub && sub !== "overlay-templates")) {
    return <OverlayTemplateEditor />;
  }
  return <OverlayTemplatesList />;
}
